// Helper functions for swell tooltip functionality

/**
 * Get compass angle from direction string
 * @param {string} direction - Compass direction (e.g., 'N', 'NE', 'SE')
 * @returns {number} Angle in degrees
 */
export function getCompassAngle(direction) {
  const compassDirections = {
    N: 0,
    NNE: 22.5,
    NE: 45,
    ENE: 67.5,
    E: 90,
    ESE: 112.5,
    SE: 135,
    SSE: 157.5,
    S: 180,
    SSW: 202.5,
    SW: 225,
    WSW: 247.5,
    W: 270,
    WNW: 292.5,
    NW: 315,
    NNW: 337.5,
  };

  return compassDirections[direction.toUpperCase()] || 0;
}

/**
 * Apply repulsion technique to prevent overlapping swells
 * @param {Array} swells - Array of swell objects
 * @param {number} minSeparation - Minimum separation in degrees (default: 60)
 * @returns {Array} Adjusted swell positions
 */
export function handleRepulsion(swells, minSeparation = 60) {
  const validSwells = swells.filter(swell => swell != null);

  const positions = validSwells.map((swell, index) => ({
    ...swell,
    positionAngle: swell.angle,
    index: index,
  }));

  for (let i = 0; i < positions.length - 1; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      let angleDifference = Math.abs(
        positions[i].positionAngle - positions[j].positionAngle
      );

      if (angleDifference > 180) {
        angleDifference = 360 - angleDifference;
      }

      if (angleDifference < minSeparation) {
        const separation = (minSeparation - angleDifference) / 2;

        if (positions[i].positionAngle < positions[j].positionAngle) {
          positions[i].positionAngle -= separation;
          positions[j].positionAngle += separation;
        } else {
          positions[i].positionAngle += separation;
          positions[j].positionAngle -= separation;
        }

        positions[i].positionAngle = (positions[i].positionAngle + 360) % 360;
        positions[j].positionAngle = (positions[j].positionAngle + 360) % 360;
      }
    }
  }

  return positions;
}

/**
 * Get icon position based on angle
 * @param {number} angle - Angle in degrees
 * @param {boolean} isSheltered - Whether the wave is sheltered
 * @returns {Object} Position object with top and right values
 */
export function getIconPosition(angle, isSheltered = false) {
  const isMobile = window.innerWidth <= 768;

  const angleInRadians = (angle * Math.PI) / 180;
  const offsetX = 40 * Math.cos(angleInRadians);
  const offsetY = 30 * Math.sin(angleInRadians);

  const centerPosition = isMobile
    ? { top: 80, right: 88 }
    : { top: 42 * 3.5, right: 110 };

  const distanceMultiplier = isSheltered ? 2 : 1;
  const adjustedOffsetX = offsetX * distanceMultiplier;
  const adjustedOffsetY = offsetY * distanceMultiplier;

  const positionTop =
    angle >= 180
      ? centerPosition.top - adjustedOffsetY
      : centerPosition.top + adjustedOffsetY;
  const positionRight =
    angle >= 180
      ? centerPosition.right - adjustedOffsetX
      : centerPosition.right + adjustedOffsetX;

  return { top: positionTop, right: positionRight };
}

/**
 * Transform wind angle for display
 * @param {number} angle - Wind angle in degrees
 * @returns {number} Transformed angle
 */
export function windDeg(angle) {
  if (angle >= 180) {
    return angle - 180;
  } else {
    return angle + 180;
  }
}

/**
 * Check if there are any sheltered waves in the data
 * @param {Array} data - Array of swell data
 * @returns {boolean} True if sheltered waves exist
 */
export function hasShelteredWaves(data) {
  return data.some(item => item && item.height < 0);
}

/**
 * Create a sheltered wave message element
 * @param {string} message - Message text
 * @param {string} backgroundColor - Background color (default: 'red')
 * @returns {HTMLElement} Message element
 */
export function createShelteredWaveMessage(
  message,
  withMap = false,
  shelteredWave
) {
  // Swell icon color logic ported from Ruby
  function getSwellIconColor(swell) {
    if (!swell || !swell.period) {
      return 'rgb(170,170,170)'; // PERIOD_NONE_COLOR
    }

    const PERIOD_REFERENCE_COLORS = [
      [0, [220, 220, 255]],
      [2, [220, 220, 255]],
      [4, [180, 180, 255]],
      [6, [150, 150, 255]],
      [8, [100, 100, 255]],
      [10, [50, 50, 255]],
      [12, [0, 0, 255]],
      [14, [0, 0, 200]],
      [16, [0, 0, 150]],
      [18, [0, 0, 100]],
      [20, [0, 0, 140]],
      [22, [0, 0, 50]],
    ];

    const PERIOD_LONGEST = 22;
    const PERIOD_EVEN_LONGER_COLOR = [0, 0, 0];

    function interpolatedPeriodColor(period) {
      if (period > PERIOD_LONGEST) {
        return PERIOD_EVEN_LONGER_COLOR;
      }

      // Find the two reference colors to interpolate between
      for (let i = 0; i < PERIOD_REFERENCE_COLORS.length - 1; i++) {
        const [lowerThreshold, lowerColor] = PERIOD_REFERENCE_COLORS[i];
        const [upperThreshold, upperColor] = PERIOD_REFERENCE_COLORS[i + 1];

        if (period >= lowerThreshold && period <= upperThreshold) {
          if (period === upperThreshold) return upperColor;
          if (period === lowerThreshold) return lowerColor;

          // Interpolate between the two colors
          const t =
            (upperThreshold - period) / (upperThreshold - lowerThreshold);
          return [0, 1, 2].map(j =>
            Math.round(lowerColor[j] * t + upperColor[j] * (1 - t))
          );
        }
      }

      return PERIOD_REFERENCE_COLORS[PERIOD_REFERENCE_COLORS.length - 1][1];
    }

    const colorValues = interpolatedPeriodColor(swell.period);
    const isSheltered = swell.height < 0;

    if (isSheltered) {
      // Exchange blue and red components for sheltered waves
      return `rgb(${colorValues[2]},${colorValues[1]},${colorValues[0]})`;
    } else {
      return `rgb(${colorValues.join(',')})`;
    }
  }

  const redMessage = document.createElement('div');
  redMessage.className = `swell-tooltip__sheltered-message ${
    withMap ? 'swell-tooltip__sheltered-message--with-map' : ''
  }`;
  redMessage.style.backgroundColor = getSwellIconColor(shelteredWave);
  redMessage.innerText = message;
  return redMessage;
}
